import React, { useState, useEffect } from 'react';
import words from 'an-array-of-english-words';
import Keyboard from './Keyboard';

const WordleGame = () => {
  // Predefined words for each level
  const levelWords = [
    'AYATI',
    'TIZZI',
    'IBATT',
    'YABAY',
    'BHBEK',
    'AKABA',
    'BKHAF',
    'TAKHT',
    'BAHER',
    'HAYAT'
  ];

  const [currentWord, setCurrentWord] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameWon, setGameWon] = useState(false);
  const [gameLost, setGameLost] = useState(false);
  const [level, setLevel] = useState(1);
  const [progress, setProgress] = useState({});
  const [usedLetters, setUsedLetters] = useState({});
  const [gameCompleted, setGameCompleted] = useState(false);

  // Filter 5-letter words and add level words to the dictionary
  const fiveLetterWords = [
    ...new Set([
      ...words.filter(word => word.length === 5),
      ...levelWords.map(word => word.toLowerCase())
    ])
  ];

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem('wordleProgress');
    if (savedProgress) {
      const parsedProgress = JSON.parse(savedProgress);
      setProgress(parsedProgress);
      setLevel(parsedProgress.currentLevel || 1);
    }
    generateNewWord();
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const generateNewWord = () => {
    if (level > levelWords.length) {
      setGameCompleted(true);
      return;
    }
    setCurrentWord(levelWords[level - 1]);
    setGuesses([]);
    setCurrentGuess('');
    setGameWon(false);
    setGameLost(false);
    setUsedLetters({});
  };

  const updateUsedLetters = (guess) => {
    const newUsedLetters = { ...usedLetters };
    for (let i = 0; i < guess.length; i++) {
      const letter = guess[i];
      if (letter === currentWord[i]) {
        newUsedLetters[letter] = 'correct';
      } else if (currentWord.includes(letter)) {
        if (newUsedLetters[letter] !== 'correct') {
          newUsedLetters[letter] = 'present';
        }
      } else {
        newUsedLetters[letter] = 'absent';
      }
    }
    setUsedLetters(newUsedLetters);
  };

  const handleKeyPress = (e) => {
    if (gameWon || gameLost || gameCompleted) return;

    if (e.key === 'Enter' && currentGuess.length === 5) {
      if (!fiveLetterWords.includes(currentGuess.toLowerCase())) {
        alert('Not a valid word!');
        return;
      }

      const newGuesses = [...guesses, currentGuess];
      setGuesses(newGuesses);
      updateUsedLetters(currentGuess);

      if (currentGuess === currentWord) {
        setGameWon(true);
        const newProgress = {
          ...progress,
          currentLevel: level + 1,
          [`level${level}`]: true
        };
        setProgress(newProgress);
        localStorage.setItem('wordleProgress', JSON.stringify(newProgress));
        setLevel(level + 1);
        setTimeout(generateNewWord, 2000);
      } else if (newGuesses.length >= 6) {
        setGameLost(true);
        setTimeout(() => {
          setLevel(level + 1);
          generateNewWord();
        }, 2000);
      }

      setCurrentGuess('');
    } else if (e.key === 'Backspace') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (/^[A-Za-z]$/.test(e.key) && currentGuess.length < 5) {
      setCurrentGuess(prev => prev + e.key.toUpperCase());
    }
  };

  const getLetterColor = (letter, index, guess) => {
    if (letter === currentWord[index]) return 'bg-green-500';
    if (currentWord.includes(letter)) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  if (gameCompleted) {
    return (
      <div className="h-screen bg-gray-900 text-white flex flex-col justify-center items-center p-4">
        <h1 className="text-4xl sm:text-6xl font-bold text-center mb-8">IBAT AYATI</h1>
        <div className="text-6xl sm:text-8xl mb-8">❤️</div>
        <p className="text-xl sm:text-2xl text-center text-gray-400">
          Congratulations! You've completed all levels!
        </p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 text-white p-2 sm:p-4 flex flex-col overflow-hidden">
      <div className="max-w-md mx-auto w-full flex flex-col h-full">
        <div className="flex-none">
          <h1 className="text-3xl sm:text-4xl font-bold text-center mb-4 sm:mb-8">AYARDLE</h1>
          <div className="text-center mb-2 sm:mb-4">
            <p className="text-lg sm:text-xl">Level: {level} of {levelWords.length}</p>
            <p className="text-xs sm:text-sm text-gray-400">Progress: {Object.keys(progress).filter(k => k.startsWith('level')).length} levels completed</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-start">
          <div className="grid grid-rows-6 gap-1.5 sm:gap-2 mb-2 sm:mb-4 mx-auto">
            {[...Array(6)].map((_, rowIndex) => (
              <div key={rowIndex} className="grid grid-cols-5 gap-1.5 sm:gap-2">
                {[...Array(5)].map((_, colIndex) => {
                  const letter = guesses[rowIndex]?.[colIndex] || '';
                  const color = guesses[rowIndex] ? getLetterColor(letter, colIndex, guesses[rowIndex]) : '';
                  return (
                    <div
                      key={colIndex}
                      className={`w-12 h-12 sm:w-14 sm:h-14 border-2 border-gray-600 flex items-center justify-center text-2xl sm:text-3xl font-bold ${color}`}
                    >
                      {letter}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-5 gap-1.5 sm:gap-2 mb-2 sm:mb-4 mx-auto">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-gray-600 flex items-center justify-center text-2xl sm:text-3xl font-bold"
              >
                {currentGuess[index] || ''}
              </div>
            ))}
          </div>

          {(gameWon || gameLost) && (
            <div className="text-center mb-2 sm:mb-4">
              <p className="text-lg sm:text-xl font-bold">
                {gameWon ? 'Congratulations! 🎉' : 'Game Over! 😢'}
              </p>
              <p className="text-xs sm:text-sm text-gray-400">
                The word was: {currentWord}
              </p>
            </div>
          )}
        </div>

        <div className="flex-none mt-auto">
          <Keyboard onKeyPress={handleKeyPress} usedLetters={usedLetters} />
        </div>
      </div>
    </div>
  );
};

export default WordleGame; 